// src/components/layout/Sidebar.jsx
// Updated version: Route "/" displays as "Home" in sidebar

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Plug,
  BarChart3,
  LayoutDashboard,
  Database,
  Layers,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const navigation = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Connectors', href: '/connectors', icon: Plug },
  { name: 'Dashboards', href: '/dashboards', icon: LayoutDashboard },
  { name: 'Charts', href: '/charts', icon: BarChart3 },
  { name: 'Data Explorer', href: '/explorer', icon: Database },
  { name: 'Model', href: '/model', icon: Layers },
  { name: 'Logs', href: '/logs', icon: FileText },
  { name: 'Admin', href: '/admin', icon: Settings },
];

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  
  // Get user from localStorage
  const userEmail = localStorage.getItem('user_email') || 'admin@example.com';
  const userName = userEmail.split('@')[0];
  const userInitials = userName.substring(0, 2).toUpperCase();
    const userRoles = (() => {
    try { return JSON.parse(localStorage.getItem('user_roles') || '[]'); }
    catch { return []; }
  })();
  const isSuperAdmin = userRoles.includes('super_admin');


  const handleSignOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_roles');
    window.location.href = '/login';
  };

  return (
    <aside
      className={cn(
        'relative flex flex-col bg-[#141414] border-r border-[#2a2a2a]',
        'transition-all duration-300 ease-in-out',
        collapsed ? 'w-[72px]' : 'w-[248px]'
      )}
    >
      {/* Logo - ONLY CHANGE: replaced placeholder with real logos */}
      <div className="h-16 flex items-center px-6 border-b border-[#2a2a2a] flex-shrink-0">
        {!collapsed ? (
          <div className="flex items-center gap-2.5">
            <img 
              src="/connexlogo2-transparent.png" 
              alt="Connex" 
              className="h-10 w-auto"
            />
          </div>
        ) : (
          <img 
            src="/symbol-transparent.png" 
            alt="Connex" 
            className="w-10 h-10 mx-auto object-contain"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {navigation.map((item) => {
          // 14a-3: a page under an entry (a chart, a dashboard) lights that entry too.
          const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(`${item.href}/`));
          const Icon = item.icon;
          
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-3 h-11 px-3 rounded-xl text-sm font-medium transition-all',
                isActive
                  ? 'bg-gradient-to-r from-blue-500/10 to-violet-500/10 text-white'
                  : 'text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a]'
              )}
              title={collapsed ? item.name : undefined}
            >
              <Icon 
                className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isActive && 'text-blue-400'
                )} 
              />
              {!collapsed && (
                <span className="truncate">{item.name}</span>
              )}
            </Link>
          );
        })}
      </nav>
      

      {/* Bottom Section */}
      <div className="border-t border-[#2a2a2a] flex-shrink-0">
        
        {/* Settings & Logout */}
        <div className="px-3 pt-3 pb-2 space-y-1">
                    {isSuperAdmin && (
            <Link
              to="/super-admin"
              className={cn(
                'flex items-center gap-3 h-11 px-3 rounded-xl text-sm font-medium transition-all',
                location.pathname.startsWith('/super-admin')
                  ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-white'
                  : 'text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a]'
              )}
              title={collapsed ? 'Super Admin' : undefined}
            >
              <ShieldCheck
                className={cn(
                  'w-5 h-5 flex-shrink-0',
                  location.pathname.startsWith('/super-admin') && 'text-amber-400'
                )}
              />
              {!collapsed && <span>Super Admin</span>}
            </Link>
          )}


          <Link
            to="/settings"
            className={cn(
              'flex items-center gap-3 h-11 px-3 rounded-xl text-sm font-medium transition-all',
              location.pathname === '/settings'
                ? 'bg-gradient-to-r from-blue-500/10 to-violet-500/10 text-white'
                : 'text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a]'
            )}
            title={collapsed ? 'Settings' : undefined}
          >
            <Settings 
              className={cn(
                'w-5 h-5 flex-shrink-0',
                location.pathname === '/settings' && 'text-blue-400'
              )} 
            />
            {!collapsed && <span>Settings</span>}
          </Link>

          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 w-full h-11 px-3 rounded-xl text-sm font-medium text-[#a0a0a0] hover:text-red-400 hover:bg-[#1a1a1a] transition-all"
            title={collapsed ? 'Sign out' : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>

        {/* User Account */}
        <div className="px-3 py-3 border-t border-[#2a2a2a]">
          <div className="flex items-center gap-3 h-11 px-3 rounded-xl hover:bg-[#1a1a1a] transition-colors cursor-pointer">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-violet-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-white">
                {userInitials}
              </span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {userName}
                </p>
                <p className="text-xs text-[#6a6a6a] truncate">
                  {userEmail}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <div className="px-3 pb-3">
          <button
            onClick={onToggle}
            className="flex items-center justify-center w-full h-11 px-3 rounded-xl text-sm font-medium text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a] transition-all"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <div className="flex items-center gap-3 w-full">
                <ChevronLeft className="w-5 h-5" />
                <span>Collapse</span>
              </div>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}